import { AntiforgeryTokenMissingError } from './http/errors.js';
/**
 * Name of the meta element the host renders the request token into (ADR-017 D2).
 *
 * Frozen as a cross-language contract, exactly as the client configuration element id is: a
 * consumer needs this name, `getAttribute('content')` and nothing else.
 */
export const ANTIFORGERY_META_NAME = 'bedrock-antiforgery-token';
/**
 * Request header the token travels in (ADR-017 D3).
 *
 * Pinned on both sides — `AddBedrockWeb` sets `AntiforgeryOptions.HeaderName` to this literal —
 * so the name is an agreement rather than a framework default that could move.
 */
export const ANTIFORGERY_HEADER_NAME = 'RequestVerificationToken';
/** Methods that change nothing and therefore never carry the token. */
const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];
export function isSafeMethod(method) {
    return SAFE_METHODS.includes(method.toUpperCase());
}
/**
 * Reads the token the page currently carries, or null when it rendered none.
 *
 * Read on every call and never cached: M7 replaces the meta element's content in place after an
 * identity transition, and a cached value would keep sending the previous principal's token
 * (ADR-017 D7).
 */
export function readAntiforgeryToken(documentRef = document) {
    const meta = documentRef.querySelector(`meta[name="${ANTIFORGERY_META_NAME}"]`);
    const token = meta?.getAttribute('content') ?? '';
    return token.length === 0 ? null : token;
}
/**
 * Applies the token to a request's headers, after everything the caller supplied.
 *
 * Last on purpose: a caller that passed its own `headers` must not be able to clear or override
 * the header on a same-origin unsafe request, whether by accident or otherwise.
 *
 * @throws {AntiforgeryTokenMissingError} if the page rendered no token.
 */
export function applyAntiforgeryHeader(headers, documentRef = document) {
    const token = readAntiforgeryToken(documentRef);
    if (token === null) {
        throw new AntiforgeryTokenMissingError(`This request needs the antiforgery token, but the page rendered no <meta name="${ANTIFORGERY_META_NAME}">. ` +
            'Render it with @Html.BedrockAntiforgeryToken() in the layout.');
    }
    headers.set(ANTIFORGERY_HEADER_NAME, token);
}
//# sourceMappingURL=antiforgery.js.map